import {
  signInWithEmailAndPassword, 
  sendPasswordResetEmail, 
  signOut, 
  signInWithRedirect, 
  signInWithPopup, 
  createUserWithEmailAndPassword,
  OAuthProvider,
  getAuth
} from 'firebase/auth';
import { getDatabase, ref, get, set, update } from 'firebase/database';
import { db, auth, googleAuthProvider, facebookAuthProvider } from 'auth/FirebaseAuth';
import logError from '../utils/logger';
import debug from '../utils/debugLog';

// Caracteres que RTDB no acepta en el nombre de un hijo.
const RTDB_KEY_INVALIDA = /[.#$[\]/]/;

const pickNonEmpty = (...values) => values.find(v => v !== undefined && v !== null && v !== '');

// Une el/los curso(s) que ya tiene el nodo con el del launch de LTI actual, sin duplicar.
//
// Un alumno puede estar matriculado en más de una asignatura, pero /Admision guarda una
// sola fila por email, así que la segunda sólo se conoce por el launch. Si en cada login
// se reemplazara el valor, quedaría viendo únicamente los bloques del curso desde el que
// entró y perdería los de la otra asignatura.
//
// Devuelve un string cuando queda un solo curso, para no cambiarle la forma a los nodos
// de una sola asignatura, que son la mayoría. getBloques acepta ambas formas e intersecta.
//
// Del valor previo se conserva sólo lo que tiene forma de código de asignatura. Así no
// se arrastran a la unión ni 'None' (lo que se escribe cuando el context.label es
// inválido) ni 'curso' (el placeholder de AdmisionPlantilla, que traen los nodos que
// nacieron sin fila en /Admision). Se filtra por forma y no por lista negra para que
// cualquier otro valor espurio quede fuera igual.
//
// El curso del launch se agrega sin validar, igual que hacía el código anterior al
// escribirlo directo: si LTI manda algo raro, esto no cambia ese comportamiento.
//
// La unión no se poda sola: si alguien deja una asignatura, hay que escribirle el valor
// correcto a mano. Es el riesgo preferible frente al de que un alumno no vea material en
// el que está matriculado.
const CODIGO_CURSO = /^[A-Z]{3}\d{4}$/;
const unirCurso = (actual, nuevo) => {
  if (!nuevo) return actual;   // el llamador ya lo garantiza; devolver actual es un no-op
  const previos = [...new Set((Array.isArray(actual) ? actual : [actual])
    .filter(c => typeof c === 'string' && CODIGO_CURSO.test(c)))];
  const union = previos.includes(nuevo) ? previos : [...previos, nuevo];
  return union.length === 1 ? union[0] : union;
};

const FirebaseService = {
  signInEmailRequest: async (email, password) => 
    signInWithEmailAndPassword(auth, email, password)
      .then(user => user)
      .catch(err => err),

  forgetPassword: async (email) => 
    sendPasswordResetEmail(auth, email)
      .then(user => user)
      .catch(err => err),

  signOutRequest: async () => 
    signOut(auth)
      .then(user => user)
      .catch(err => err),

  signInOpenIdRequest: async (provider) => {
    try {
      const result = await signInWithPopup(auth, provider);
      // User is signed in.
      // IdP data available using getAdditionalUserInfo(result)
      // Get the OAuth access token and ID Token
      const credential = OAuthProvider.credentialFromResult(result);
      const accessToken = credential.accessToken;
      const idToken = credential.idToken;
      return result;
    } catch (error) {
      // Handle error.
      //Enviar error a log
      logError(error);
      console.error("Error during OpenID sign-in:", error);
      return { message: error.message, code: error.code };
    }
  },

  signInGoogleRequest: async () => 
    signInWithPopup(auth, googleAuthProvider)
      .then(user => user)
      .catch(err => err),

  signInFacebookRequest: async () => 
    signInWithPopup(auth, facebookAuthProvider)
      .then(user => user)
      .catch(err => err),

  signUpEmailRequest: async (email, password) => 
    createUserWithEmailAndPassword(auth, email, password)
      .then(user => user)
      .catch(err => err),

  // Llamadas a DB
  // Devuelve el snapshot de Clients/{uid} exista o no el nodo. Los llamadores
  // detectan la ausencia con snap.val() === null y cada uno decide qué hacer, así
  // que esto no puede lanzar cuando el nodo simplemente no está: en el login por
  // OIDC esa ausencia es transitoria y esperada, porque el nodo lo escribe la Cloud
  // Function onNewUserCreated (trigger auth.user().onCreate) desde
  // /UsuariosBase/{provider} de forma asíncrona. Si acá se lanza, el reintento de
  // signInWithOpenID queda inalcanzable y el primer login de todo usuario nuevo
  // falla con "No data available".
  //
  // Un fallo real de lectura (red, permisos) sí se registra y se propaga.
  getPrimaryUserData: async (uid) => {
    try {
      return await get(ref(db, `Clients/${uid}`));
    } catch (err) {
      logError(err);
      throw err;
    }
  },

  getDisabledProjectInfo: async (projectId) => {
    try {
      debug.log('[DisabledCheck] Querying:', `Config/DisabledProjects/${projectId}`);
      const snap = await get(ref(db, `Config/DisabledProjects/${projectId}`));
      debug.log('[DisabledCheck] snap.exists():', snap.exists(), 'val:', snap.val());
      if (snap.exists()) {
        return snap.val();
      }
      return null;
    } catch (err) {
      console.error('[DisabledCheck] Error:', err);
      logError(err);
      return null;
    }
  },

  getSecondaryUserData: (secondary, uid) =>
    get(ref(getDatabase(secondary), `Usuarios/${uid}/Submenu`))
      .then(snap => snap)
      .catch(err => err),

  // Garantiza que el usuario que hace login tenga nodo en Usuarios/{uid} de la DB
  // secundaria, y reconcilia sus secuencias contra Settings/SecuenciasIniciales, que
  // actúa como fuente de verdad del acceso. Corre en todos los proyectos.
  //
  // - Si el usuario NO existe: lo crea con datos mínimos (Nombre, Email, Rol tomados de
  //   la primaria) más las secuencias iniciales. No inventa Curso/Seccion/Sede: esos
  //   campos son del flujo LTI y los escribe el launch correspondiente.
  // - Si el usuario YA existe: solo agrega las secuencias que le falten. Nunca
  //   sobrescribe una existente, porque cada entrada guarda el progreso del alumno en
  //   Avances (lo escriben las apps Unity, no la plataforma), y no toca ningún otro campo.
  //
  // Si el proyecto no define Settings/SecuenciasIniciales, el usuario igual se crea, solo
  // que sin Secuencias.
  //
  // Retorna { creado, secuenciasAgregadas }. Nunca lanza: un fallo aquí no debe bloquear
  // el login.
  ensureUsuarioSecundario: async (secondary, uid, primaryProfile, email) => {
    const resultadoVacio = { creado: false, secuenciasAgregadas: 0 };
    try {
      if (!secondary || !uid) return resultadoVacio;
      const secondaryDb = getDatabase(secondary);
      const usuarioRef = ref(secondaryDb, `Usuarios/${uid}`);

      // Lecturas acotadas: nunca traer Usuarios/{uid} completo (arrastra Actividades).
      // Email/Nombre solo se leen para confirmar que el usuario existe.
      const [secuenciasSnap, emailSnap, nombreSnap, inicialesSnap] = await Promise.all([
        get(ref(secondaryDb, `Usuarios/${uid}/Secuencias`)),
        get(ref(secondaryDb, `Usuarios/${uid}/Email`)),
        get(ref(secondaryDb, `Usuarios/${uid}/Nombre`)),
        get(ref(secondaryDb, 'Settings/SecuenciasIniciales'))
      ]);

      const inicialesRaw = inicialesSnap.val();
      const iniciales = inicialesRaw && typeof inicialesRaw === 'object' ? inicialesRaw : {};

      // Descarta claves que RTDB no acepta como nombre de hijo, en vez de dejar que
      // reviente el write completo por una sola secuencia mal nombrada.
      const seqUids = Object.keys(iniciales).filter(seqUid => {
        if (RTDB_KEY_INVALIDA.test(seqUid)) {
          debug.warn('[ensureUsuarioSecundario] Clave inválida para RTDB, se omite:', seqUid);
          return false;
        }
        return true;
      });

      const existe = secuenciasSnap.exists() || emailSnap.exists() || nombreSnap.exists();

      if (!existe) {
        const correo = pickNonEmpty(primaryProfile?.Email, email);
        const rol = pickNonEmpty(primaryProfile?.Rol) || 'Alumno';
        const nuevoUsuario = {
          uid,
          Nombre: pickNonEmpty(primaryProfile?.Nombre) || `Usuario ${uid}`,
          Rol: rol,
          role: rol,
          TimestampInicio: Date.now(),
          TimestampFinal: Date.now() + 305 * 24 * 60 * 60 * 1000,
          CreadoEnLogin: true
        };
        if (correo) {
          nuevoUsuario.Email = correo;
          nuevoUsuario.user = correo;
        }
        seqUids.forEach(seqUid => {
          nuevoUsuario[`Secuencias/${seqUid}`] = iniciales[seqUid];
        });

        // update y no set: si una app Unity ya escribió algo bajo este uid, no se pisa.
        await update(usuarioRef, nuevoUsuario);
        debug.log('[ensureUsuarioSecundario] Usuario creado en secundaria:', uid, nuevoUsuario.Nombre);
        return { creado: true, secuenciasAgregadas: seqUids.length };
      }

      // Secuencias puede venir como '-' en datos antiguos: se trata como vacío.
      const actuales = secuenciasSnap.val();
      const yaAsignadas = actuales && typeof actuales === 'object' ? actuales : {};

      // Update por rutas hijas: no toca las secuencias hermanas ya presentes.
      const faltantes = {};
      seqUids.forEach(seqUid => {
        if (!yaAsignadas[seqUid]) faltantes[`Secuencias/${seqUid}`] = iniciales[seqUid];
      });

      const total = Object.keys(faltantes).length;
      if (total === 0) return resultadoVacio;

      await update(usuarioRef, faltantes);
      debug.log('[ensureUsuarioSecundario] Secuencias agregadas a', uid, Object.keys(faltantes));
      return { creado: false, secuenciasAgregadas: total };
    } catch (err) {
      logError(err);
      debug.log('[ensureUsuarioSecundario] Error:', err);
      return resultadoVacio;
    }
  },

  checkEmailInSecondaryDB: async (uid, email, secondary, primaryProfile) => {
    try {
      const secondaryDb = getDatabase(secondary);

      const primaryLtiData = primaryProfile ? {
        curso: primaryProfile.Curso,
        seccion: primaryProfile.Seccion || primaryProfile.SeccionSap,
        sede: primaryProfile.Sede,
        nombre: primaryProfile.Nombre,
        role: primaryProfile.Rol,
        email: primaryProfile.Email || email
      } : null;
      
      // Obtener datos de LTI desde los claims del token del usuario autenticado
      let ltiData = null;
      // contextValid: el backend marca labelValid=false cuando context.label no tiene
      // el formato esperado. En ese caso forzamos Curso='None' para que la plataforma
      // no muestre ningún curso. Por defecto true (no afecta logins no-LTI).
      let contextValid = true;
      try {
        const currentUser = auth.currentUser;
        if (currentUser && currentUser.uid === uid) {
          // Force refresh so we read the latest custom claims after LTI login.
          const idTokenResult = await currentUser.getIdTokenResult(true);
          const claims = idTokenResult.claims;
          debug.log('[FirebaseService] Token claims:', claims);
          if (claims.labelValid === false) contextValid = false;
          if (claims.lti) {
            ltiData = {
              curso: claims.curso,
              seccion: claims.seccion,
              sede: claims.sede ?? claims.Sede,
              nombre: claims.nombre || '',
              role: claims.role || '',
              email: claims.email || email
            };
            debug.log('[FirebaseService] LTI data from token claims:', ltiData);
          }
        } else if (currentUser && currentUser.uid !== uid) {
          debug.warn('[FirebaseService] Skipping auth.currentUser claims due to uid mismatch:', { expectedUid: uid, authUid: currentUser.uid });
        }
      } catch (err) {
        debug.log('[FirebaseService] Error reading token claims:', err);
      }

      // Para campos de contexto LTI (curso, seccion, sede), los claims del token
      // tienen prioridad sobre la Primary DB, ya que los claims provienen directamente
      // del launch LTI y la lectura de Primary DB puede devolver datos cacheados/viejos.
      const canonicalLtiData = {
        curso: pickNonEmpty(ltiData?.curso, primaryLtiData?.curso),
        seccion: pickNonEmpty(ltiData?.seccion, primaryLtiData?.seccion),
        sede: pickNonEmpty(ltiData?.sede, primaryLtiData?.sede),
        nombre: pickNonEmpty(primaryLtiData?.nombre, ltiData?.nombre),
        role: pickNonEmpty(primaryLtiData?.role, ltiData?.role),
        email: pickNonEmpty(primaryLtiData?.email, ltiData?.email, email)
      };

      const canonicalSede = canonicalLtiData.sede;

      // Verificar si el usuario ya existe en la colección Usuarios
      const usuariosRef = ref(secondaryDb, `Usuarios/${uid}`);
      const usuariosSnap = await get(usuariosRef);

      if (usuariosSnap.exists()) {
        // Usuario ya existe: actualizar campos LTI disponibles, incluyendo Sede aunque no vengan curso/seccion.
        if (canonicalLtiData) {
          const updatePayload = {};
          // Contexto inválido → Curso='None' (la plataforma no mostrará ningún curso).
          if (!contextValid) {
            updatePayload.Curso = 'None';
          } else if (canonicalLtiData.curso) {
            // Se AGREGA, no se reemplaza: el alumno matriculado en dos asignaturas sólo
            // tiene una en /Admision, y la otra se descubre por el launch. Ver unirCurso.
            const unido = unirCurso(usuariosSnap.val()?.Curso, canonicalLtiData.curso);
            if (JSON.stringify(unido) !== JSON.stringify(usuariosSnap.val()?.Curso)) {
              updatePayload.Curso = unido;
            }
          }
          if (canonicalLtiData.seccion) updatePayload.Seccion = canonicalLtiData.seccion;
          if (canonicalLtiData.nombre) updatePayload.Nombre = canonicalLtiData.nombre;
          if (canonicalLtiData.role) updatePayload.Rol = canonicalLtiData.role;
          if (canonicalSede !== undefined && canonicalSede !== null) updatePayload.Sede = canonicalSede;

          if (Object.keys(updatePayload).length > 0) {
            debug.log('[FirebaseService] Updating existing user with LTI fields:', updatePayload);
            await update(usuariosRef, updatePayload);
          }
        }
        return true;
      }

      // Usuario no existe: crearlo
      {
        // Si hay datos de LTI, crear usuario directamente con esos datos
        if (canonicalLtiData.curso && canonicalLtiData.seccion) {
          debug.log('[FirebaseService] Creating user from LTI data');
          const plantillaRef = ref(secondaryDb, `AdmisionPlantilla`);
          const plantillaSnap = await get(plantillaRef);
          const plantillaData = plantillaSnap.exists() ? plantillaSnap.val() : {};

          const nuevoUsuario = {
            ...plantillaData,
            Curso: canonicalLtiData.curso,
            Email: canonicalLtiData.email || `${uid}@noemail.local`,
            Nombre: canonicalLtiData.nombre || `Usuario ${uid}`,
            Seccion: canonicalLtiData.seccion,
            Sede: canonicalSede ?? plantillaData.Sede ?? '',
            Rol: canonicalLtiData.role || plantillaData.Rol || 'Alumno',
            role: canonicalLtiData.role || plantillaData.Rol || 'Alumno',
            user: canonicalLtiData.email || `${uid}@noemail.local`,
            uid: uid,
            TimestampInicio: Date.now(),
            TimestampFinal: Date.now() + 30 * 24 * 60 * 60 * 1000
          };

          await set(usuariosRef, nuevoUsuario);
          debug.log('[FirebaseService] User created from LTI:', nuevoUsuario);
          return true;
        }

        // Si no hay datos LTI completos, buscar en Admision
        const isFakeEmail = !email || email.includes('@fake.lti') || email.includes('@noemail.local');
        let admisionSnap = null;
        
        if (!isFakeEmail) {
          const cleanedEmail = email.replace(/[.#$[\]]/g, '');
          const admisionRef = ref(secondaryDb, `Admision/${cleanedEmail}`);
          admisionSnap = await get(admisionRef);
        }

        // Si existe admision, usarla
        if (admisionSnap && admisionSnap.exists()) {
          const admisionData = admisionSnap.val();
          const plantillaRef = ref(secondaryDb, `AdmisionPlantilla`);
          const plantillaSnap = await get(plantillaRef);
          const plantillaData = plantillaSnap.exists() ? plantillaSnap.val() : {};

          const nuevoUsuario = {
            ...plantillaData,
            Curso: admisionData.curso || plantillaData.Curso || 'AIA1101',
            Email: admisionData.email ?? email ?? `${uid}@noemail.local`,
            Nombre: admisionData.nombre || plantillaData.Nombre || `Usuario ${uid}`,
            Seccion: admisionData.seccion || plantillaData.Seccion || '',
            Sede: canonicalSede ?? admisionData.sede ?? plantillaData.Sede ?? '',
            Rol: canonicalLtiData.role || admisionData.rol || plantillaData.Rol || 'Alumno',
            role: canonicalLtiData.role || admisionData.rol || plantillaData.Rol || 'Alumno',
            user: admisionData.email ?? email ?? `${uid}@noemail.local`,
            uid: uid,
            TimestampInicio: Date.now(),
            TimestampFinal: Date.now() + 30 * 24 * 60 * 60 * 1000
          };

          await set(usuariosRef, nuevoUsuario);
        } else {
          // No existe admision ni LTI completo: intentar plantilla o crear registro mínimo
          const plantillaRef = ref(secondaryDb, `AdmisionPlantilla`);
          const plantillaSnap = await get(plantillaRef);
          if (plantillaSnap.exists()) {
            const plantillaData = plantillaSnap.val();
            const nuevoUsuario = {
              ...plantillaData,
              Curso: canonicalLtiData.curso || plantillaData.Curso || 'AIA1101',
              Email: email || `${uid}@noemail.local`,
              Nombre: canonicalLtiData.nombre || plantillaData.Nombre || `Usuario ${uid}`,
              Seccion: canonicalLtiData.seccion || plantillaData.Seccion || '',
              Sede: canonicalSede ?? plantillaData.Sede ?? '',
              Rol: canonicalLtiData.role || plantillaData.Rol || 'Alumno',
              role: canonicalLtiData.role || plantillaData.Rol || 'Alumno',
              uid,
              TimestampInicio: Date.now(),
              TimestampFinal: Date.now() + 30 * 24 * 60 * 60 * 1000
            };
            await set(usuariosRef, nuevoUsuario);
          } else {
            // Plantilla tampoco existe: crear registro mínimo
            const nuevoUsuario = {
              uid,
              Email: email || `${uid}@noemail.local`,
              Nombre: canonicalLtiData.nombre || `Usuario ${uid}`,
              Curso: canonicalLtiData.curso || 'AIA1101',
              Seccion: canonicalLtiData.seccion || '',
              Sede: canonicalSede ?? '',
              Rol: canonicalLtiData.role || 'Alumno',
              role: canonicalLtiData.role || 'Alumno',
              TimestampInicio: Date.now(),
              TimestampFinal: Date.now() + 30 * 24 * 60 * 60 * 1000
            };
            await set(usuariosRef, nuevoUsuario);
          }
        }
      }

      // Contexto inválido → forzar Curso='None' en cualquier rama de creación,
      // para que la plataforma no muestre ningún curso al usuario.
      if (!contextValid) {
        await update(usuariosRef, { Curso: 'None' });
      }

      return true; // Usuario creado o ya existente
    } catch (err) {
      logError(err);
      throw err;
    }
  }
}

export default FirebaseService;
